import React, { Fragment, useCallback, useMemo } from 'react';
import T from 'prop-types';

import RadioButtonGroup from '../../../RadioButtonGroup';
import SimpleCheckbox from '../../../Checkbox/SimpleCheckbox';
import { getGlobalSettings } from '../../../../utils/global_settings';

const fromEntityGranularity = {
  app_store: {
    kindGranularity: 'kind_app',
    storeGranularity: 'single_store',
  },
  app_unified: {
    kindGranularity: 'kind_app',
    storeGranularity: 'unified_store',
  },
  parent_company_store: {
    kindGranularity: 'kind_company_publisher',
    storeGranularity: 'single_store',
  },
  parent_company_unified: {
    kindGranularity: 'kind_company_publisher',
    storeGranularity: 'unified_store',
  },
  'include-all': {
    kindGranularity: 'kind_app',
    storeGranularity: 'single_store',
  },
  'aggregate-data': {
    kindGranularity: 'kind_company_publisher',
    storeGranularity: 'unified_store',
  },
  granularity: {
    kindGranularity: 'kind_company_publisher',
    storeGranularity: 'single_store',
  },
};

const toEntityGranularity = {
  kind_app: {
    single_store: 'app_store',
    unified_store: 'app_unified',
  },
  kind_company_publisher: {
    single_store: 'parent_company_store',
    unified_store: 'parent_company_unified',
  },
};

const kindGranularities = [['kind_company_publisher', 'Aggregate data to company/publisher'], ['kind_app', 'Show data for every app']];
const makeKindGranularitiesOptions = advancedParams => kindGranularities.map(([value, text]) => ({
  id: value,
  value,
  text,
  ...(advancedParams[value] || {}),
}));

const storeGranularities = [['unified_store', 'Unified store data'], ['single_store', 'Individual store data (iOS & Google)']];
const makeStoreGranularitiesOptions = advancedParams => storeGranularities.map(([value, text]) => ({
  id: value,
  value,
  text,
  ...(advancedParams[value] || {}),
}));

const EntityGranularity = ({
  active,
  splitByAcquisition = false,
  appsOnlySelected,
  unifiedStoreOnly = false,
  splitByAcquisitionDisabled = false,
  onChange,
}) => {
  const { kindGranularity, storeGranularity } = fromEntityGranularity[active];
  const { permissions } = getGlobalSettings();

  const handleKindGranularityChange = useCallback((value) => {
    onChange({
      data_schema: toEntityGranularity[value][storeGranularity],
      split_by_acquisition: value !== 'kind_company_publisher',
    });
  }, [storeGranularity, splitByAcquisition]);

  const handleStoreGranularityChange = useCallback((value) => {
    onChange({
      data_schema: toEntityGranularity[kindGranularity][value],
      split_by_acquisition: splitByAcquisition,
    });
  }, [kindGranularity, splitByAcquisition]);

  const kindGranularitiesOptions = useMemo(() => {
    const handleSplitByAcquisitionChange = (value) => {
      onChange({
        data_schema: active,
        split_by_acquisition: value,
      });
    };

    return makeKindGranularitiesOptions({
      kind_app: {
        ...(!permissions.allowAdvancedExportModal ? ({ subElement: (
          <div className="split-by-acquisition">
            <SimpleCheckbox
              id="split-by-acquisition"
              label="Attribute all data for apps to the first publisher/company it belonged to at beginning of reporting period"
              checked={splitByAcquisition}
              disabled={splitByAcquisitionDisabled || kindGranularity !== 'kind_app'}
              gap="12px"
              size="lg"
              className="checkbox-text-14 align-items-start"
              onChange={handleSplitByAcquisitionChange}
            />
          </div>)}) : {}),
      },
      kind_company_publisher: {
        disabled: appsOnlySelected,
      },
    });
  }, [active, kindGranularity, appsOnlySelected, splitByAcquisition]);

  const storeGranularitiesOptions = useMemo(() => (
    makeStoreGranularitiesOptions({
      single_store: { disabled: unifiedStoreOnly },
    })
  ), [unifiedStoreOnly]);

  return (
    <Fragment>
      <section className="export-settings-section">
        <div className="export-settings-section__about">
          <h4 className="h5 export-settings-section__title">Entity Aggregation</h4>
          <p className="paragraph-md export-settings-section__description">Determines how the data is presented for your selected entities.</p>
        </div>
        <div className="export-settings-section__content">
          <RadioButtonGroup
            active={kindGranularity}
            options={kindGranularitiesOptions}
            name="entity-breakdown"
            className="export-settings-section__options entity-breakdown-options"
            enabled
            onChange={handleKindGranularityChange}
          />
        </div>
      </section>
      <div className="export-settings__section-divider" />
      <section className="export-settings-section align-items-center">
        <div className="export-settings-section__about">
          <h4 className="h5 export-settings-section__title">Store granularity</h4>
          <p className="paragraph-md export-settings-section__description">Determines if data is show by each store individually, or as unified data across both app stores.</p>
        </div>
        <div className="export-settings-section__content">
          <RadioButtonGroup
            active={storeGranularity}
            options={storeGranularitiesOptions}
            name="store-breakdown"
            className="export-settings-section__options store-breakdown-options"
            enabled
            onChange={handleStoreGranularityChange}
          />
        </div>
      </section>
    </Fragment>
  );
};

EntityGranularity.propTypes = {
  active: T.string.isRequired,
  splitByAcquisition: T.bool,
  appsOnlySelected: T.bool.isRequired,
  unifiedStoreOnly: T.bool,
  splitByAcquisitionDisabled: T.bool,
  onChange: T.func.isRequired,
};

export default EntityGranularity;
